import { route } from 'preact-router';
import { useState } from 'preact/hooks';
import { Card } from '../../components/ui/Card.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { SupportLink } from '../../components/ui/SupportLink.js';
import { useAuth } from '../../context/AuthContext.js';
import { CreditCard } from './CreditCard.js';
import { PenaltiesSection } from './PenaltiesSection.js';
import { VerificationCard } from './VerificationCard.js';

interface AccountPageProps {
  path?: string;
}

interface RouteMenuItem {
  label: string;
  path: string;
}

/**
 * #451 (Weekly TechOps 2026-09-17): a lightweight WhatsApp support contact — chosen over building
 * custom in-app chat (~2 weeks of work). An EXTERNAL link, not an internal screen, so it is a
 * separate shape from `RouteMenuItem` rather than a `path` this app doesn't actually own.
 * `wa.me` deep link, not an embedded widget — the meeting's own framing was "simpler interim
 * solution". Same number already surfaced in the feedback-survey email's urgent-contact line.
 */
interface ExternalMenuItem {
  label: string;
}

const MENU: RouteMenuItem[] = [
  { label: 'My Bookings', path: '/account/bookings' },
  { label: 'My Reviews', path: '/account/reviews' },
  { label: 'Edit Profile', path: '/account/profile' },
  // 154 (#22): no 'Identity Documents' entry here any more. The verification card above leads to
  // the same screen AND says what state the documents are in, so a menu row with the same name and
  // the same destination was two controls for one thing.
  { label: 'Security', path: '/account/security' },
  // 084 FR-011: two entries, each naming what its destination does. Before 084 this menu said
  // "Notifications" and led to a push toggle — adding a list is what made that label wrong.
  { label: 'Notifications', path: '/account/notifications' },
  { label: 'Push notifications', path: '/account/notifications/push' },
];

// 403 (#1317): the address lives in `SupportLink` (WhatsApp, or the in-app chat while one runs).
const SUPPORT_MENU: ExternalMenuItem = {
  label: 'Contact Support',
};

function initialsOf(user: { first_name?: string; last_name?: string; email: string }): string {
  const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  if (name) {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('');
  }
  return (user.email[0] ?? '?').toUpperCase();
}

export function AccountPage(_props: AccountPageProps) {
  const { user, logout } = useAuth();
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  if (!user) return null;

  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();

  return (
    <div className="bg-bg px-4 py-6">
      <div className="flex items-center gap-4 mb-6">
        {user.avatar_url ? (
          <img src={user.avatar_url} alt="avatar" className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <div
            data-testid="avatar-initials"
            className="w-16 h-16 rounded-full bg-surface-mid flex items-center justify-center text-xl font-bold text-primary"
          >
            {initialsOf(user)}
          </div>
        )}
        <div>
          {fullName ? <p className="text-lg font-bold text-text">{fullName}</p> : null}
          <p className="text-sm text-gray-400">{user.email}</p>
        </div>
      </div>

      {/* 154 (#22): directly under the identity block and above the menu — the customer's own
          standing, before the list of places they can go. */}
      <VerificationCard
        emailVerifiedAt={user.email_verified_at}
        identityStatus={user.identity_verification?.status}
      />

      {/* 358 (#1091): the credit staff sent to this account's email, before the menu. */}
      <CreditCard />

      {/* 393 (#1313): the customer's penalties, absent when there are none. */}
      <PenaltiesSection />

      <div className="flex flex-col gap-2">
        {MENU.map((item) => (
          <Card key={item.path}>
            <button
              type="button"
              className="w-full flex items-center justify-between text-left text-text"
              onClick={() => route(item.path)}
            >
              <span>{item.label}</span>
              <span className="text-gray-500">›</span>
            </button>
          </Card>
        ))}
        {/* #451: last in the list — a help/support entry reads naturally as the final stop in an
            account menu, and it is the one row here that leaves the app entirely. */}
        <Card>
          <SupportLink className="w-full flex items-center justify-between text-left text-text">
            <span>{SUPPORT_MENU.label}</span>
            <span className="text-gray-500">›</span>
          </SupportLink>
        </Card>
      </div>

      <button
        type="button"
        onClick={() => setConfirmSignOut(true)}
        className="mt-8 w-full py-3 rounded-lg text-red-400 font-semibold hover:bg-surface-mid transition-colors"
      >
        Sign out
      </button>

      <ConfirmDialog
        open={confirmSignOut}
        danger
        title="Sign out?"
        body="You will need to sign in again to access your account."
        confirmLabel="Sign out"
        onCancel={() => setConfirmSignOut(false)}
        onConfirm={() => {
          setConfirmSignOut(false);
          logout();
          route('/');
        }}
      />
    </div>
  );
}
